import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { AuthContextType } from '@rfacil/shared/types';
import { useAuthStore } from '@rfacil/shared/services';

/**
 * @interface AuthProviderProps
 * @description Define las propiedades del componente AuthProvider.
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * @constant AuthContext
 * @description Contexto de autenticación.
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * @function useAuth
 * @description Hook para obtener el contexto de autenticación.
 * @returns {AuthContextType} El contexto de autenticación.
 */
export const useAuth: () => AuthContextType = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context as AuthContextType;
};

/**
 * @component AuthProvider
 * @description Proveedor de autenticación.
 * @param {React.ReactNode} children Los hijos del proveedor.
 * @returns {React.ReactElement} El componente de proveedor de autenticación.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Obtener el estado y acciones del store de Zustand
  const {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasPermission,
    getPermissions,
  } = useAuthStore();

  const value: AuthContextType = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasPermission,
    getPermissions,
  }), [user, isAuthenticated, isLoading, login, logout, hasPermission, getPermissions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
